import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Grid,
  Paper,
  makeStyles,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from '@material-ui/core';
import { getClaimsStats } from '../../services/claimsService';
import { Severity } from '../../types';
import useSnackbar from '../../hooks/useSnackbar';
import { ClaimsStats } from './ClaimDetailsTypes';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
  LineChart,
  Line,
} from 'recharts';
import { useAuthContext } from 'contexts/authContext';
// Using the newly created TenantSelector, but adapting it to Material UI v4 style if needed or wrapping it.
// Since TenantSelector is using @mui/material (v5), and this file uses @material-ui/core (v4), there might be style clashes.
// However, the user said "Project structure ... React version 17 ... @mui/material version 5.13.1 installed".
// This file imports from @material-ui/core which is v4. This implies migration or mixed usage.
// I should try to import TenantSelector and see if it works, or adapt it.
// TenantSelector was written with @mui/material.
import TenantSelector from '../../components/TenantSelector';
import PeriodSelector from '../../components/PeriodSelector/PeriodSelector';
import { ClaimsFinancialKPICards } from './ClaimsFinancialKPICards';

const useStyles = makeStyles((theme) => ({
  statsContainer: {
    marginBottom: theme.spacing(4),
  },
  statCard: {
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#1a1f36',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
  },
  totalNumber: {
    fontSize: 48,
    fontWeight: 500,
    textAlign: 'center',
    margin: theme.spacing(2, 0),
  },
  title: {
    fontSize: 16,
    fontWeight: 500,
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.8)',
  },
  chartCard: {
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#1a1f36',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    minHeight: 400,
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: 500,
    marginBottom: theme.spacing(2),
    color: 'white',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minHeight: 200,
  },
  amountValue: {
    fontSize: 32,
    fontWeight: 500,
    color: '#4caf50',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    width: '100%',

    '& span': {
      display: 'inline-block',
    },
  },
  amountLabel: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.7)',
    marginTop: theme.spacing(1),
  },
}));

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Paper
        style={{
          padding: '10px',
          border: '1px solid #ccc',
          backgroundColor: '#1a1f36',
          color: 'white',
        }}
      >
        <Typography variant="body2">{`${label}: ${payload[0].value}`}</Typography>
      </Paper>
    );
  }
  return null;
};

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

interface MonthlyPatientTypesChartProps {
  title: string;
  data: {
    year: number;
    month: number;
    inpatient: number;
    outpatient: number;
  }[];
  loading: boolean;
}

const MonthlyPatientTypesChart: React.FC<MonthlyPatientTypesChartProps> = ({
  title,
  data,
  loading,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  if (loading) {
    return (
      <Paper className={classes.chartCard}>
        <Typography className={classes.chartTitle}>{title}</Typography>
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} color="primary" />
        </div>
      </Paper>
    );
  }

  // Format month labels for display
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const formattedData = data.map((item) => {
    return {
      ...item,
      monthLabel: `${monthNames[item.month]} ${item.year}`,
    };
  });

  // Calculate Y-axis max value
  const maxValue = Math.max(
    ...formattedData.map((item) => Math.max(item.inpatient, item.outpatient)),
    1,
  );
  const yAxisMax = maxValue + Math.ceil(maxValue * 0.1); // Add 10% padding

  return (
    <Paper className={classes.chartCard}>
      <Typography className={classes.chartTitle}>{title}</Typography>
      <Box style={{ width: '100%', height: 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={formattedData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#333a56" />
            <XAxis
              dataKey="monthLabel"
              stroke="#fff"
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
            />
            <YAxis stroke="#fff" allowDecimals={false} domain={[0, yAxisMax]} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar
              dataKey="inpatient"
              name={t('Inpatient')}
              fill="#e53935"
              barSize={30}
            >
              <LabelList dataKey="inpatient" position="top" fill="#fff" />
            </Bar>
            <Bar
              dataKey="outpatient"
              name={t('ER')}
              fill="#43a047"
              barSize={30}
            >
              <LabelList dataKey="outpatient" position="top" fill="#fff" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export const ClaimsStatsComponent: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [statsData, setStatsData] = useState<ClaimsStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Changed from selectedYear to selectedPeriod (string | number)
  const [selectedPeriod, setSelectedPeriod] = useState<string | number>(
    'last-12-months',
  );

  const [currentTenant, setCurrentTenant] = useState<number>(0);
  const show = useSnackbar();
  const { user } = useAuthContext();

  const fetchStatsData = useCallback(async () => {
    try {
      setLoading(true);
      const stats = await getClaimsStats(
        selectedPeriod,
        currentTenant || undefined,
      );
      setStatsData(stats);
    } catch (error) {
      show('Failed to load claims statistics', Severity.error);
    } finally {
      setLoading(false);
    }
  }, [show, selectedPeriod, currentTenant]);

  useEffect(() => {
    fetchStatsData();
  }, [selectedPeriod, currentTenant]);

  // Prepare data for approved vs rejected chart
  const getApprovalChartData = () => {
    if (!statsData) return [];
    return [
      {
        name: 'Approved',
        value: statsData.approvedAndPartialBills,
        color: '#4caf50',
      },
      {
        name: 'Rejected',
        value: statsData.rejectedBills,
        color: '#f44336',
      },
      {
        name: 'Open',
        value: statsData.openBills,
        color: '#ff9800',
      },
    ];
  };

  // Prepare data for monthly bills chart
  const getMonthlyBillsChartData = () => {
    if (!statsData) return [];

    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    return statsData.monthlyBills.map((item) => ({
      name: `${monthNames[item.month]} ${item.year}`,
      value: item.count,
      // Store original date info for sorting if needed, but backend sends sorted
    }));
  };

  const getMonthlyPatientTypesChartData = () => {
    if (!statsData || !statsData.monthlyPatientTypes) return [];
    return statsData.monthlyPatientTypes;
  };

  // Find the maximum value to calculate percentages
  const maxValue = Math.max(
    statsData?.openBills || 0,
    statsData?.rejectedBills || 0,
    statsData?.approvedAndPartialBills || 0,
  );

  // Calculate Y-axis max value (max data value + 20)
  const yAxisMax = maxValue + 20;

  const yAxisTicks =
    maxValue > 100
      ? [
          0,
          Math.round(yAxisMax / 4),
          Math.round((2 * yAxisMax) / 4),
          Math.round((3 * yAxisMax) / 4),
          yAxisMax,
        ]
      : [0, yAxisMax];

  const { role } = user || { role: '' };

  const getPeriodLabel = () => {
    if (selectedPeriod === 'last-12-months') return t('Last 12 Months');
    if (selectedPeriod === 'last-24-months') return t('Last 24 Months');
    if (typeof selectedPeriod === 'number') return selectedPeriod;
    if (
      typeof selectedPeriod === 'string' &&
      selectedPeriod.startsWith('year-')
    ) {
      return selectedPeriod.replace('year-', '');
    }
    return selectedPeriod;
  };

  return (
    <Box className={classes.statsContainer}>
      {/* Filters: Tenant & Period Selector */}
      <Box
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        mb={3}
      >
        {role === 'super_user' && (
          <Box mr={2}>
            {/* Replaced old FormControl with TenantSelector */}
            <TenantSelector
              value={currentTenant}
              onChange={(val) => setCurrentTenant(val)}
              // includeAllOption={true} // Default is true
            />
          </Box>
        )}

        <PeriodSelector value={selectedPeriod} onChange={setSelectedPeriod} />
      </Box>

      <Grid container spacing={3}>
        {/* Financial KPI Cards */}
        <ClaimsFinancialKPICards stats={statsData} loading={loading} />

        {/* Total Bills */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper className={classes.statCard}>
            <Typography className={classes.title}>
              {t('Total Bills')}
            </Typography>
            <Typography className={classes.totalNumber}>
              {loading ? '...' : statsData?.totalBills || 0}
            </Typography>
          </Paper>
        </Grid>

        {/* Open Bills */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper className={classes.statCard}>
            <Typography className={classes.title}>{t('Open Bills')}</Typography>
            <Typography className={classes.totalNumber}>
              {loading ? '...' : statsData?.openBills || 0}
            </Typography>
          </Paper>
        </Grid>

        {/* Bills Approval Status Chart */}
        <Grid item xs={12} md={6}>
          <Paper className={classes.chartCard}>
            <Typography className={classes.chartTitle}>
              {t('Bills Status')}
            </Typography>
            {loading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress size={40} color="primary" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={getApprovalChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333a56" />
                  <XAxis dataKey="name" stroke="#fff" />
                  <YAxis
                    stroke="#fff"
                    allowDecimals={false}
                    domain={[0, (dataMax: number) => Math.ceil(dataMax + 20)]}
                    ticks={yAxisTicks}
                  />
                  <Bar dataKey="value" barSize={60}>
                    <LabelList dataKey="value" position="top" fill="#fff" />
                    {getApprovalChartData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Monthly Patient Types Chart */}
        <Grid item xs={12}>
          <MonthlyPatientTypesChart
            title={t('Monthly Inpatient vs ER')}
            data={getMonthlyPatientTypesChartData()}
            loading={loading}
          />
        </Grid>

        {/* Monthly Bills Chart */}
        <Grid item xs={12} md={6}>
          <Paper className={classes.chartCard}>
            <Typography className={classes.chartTitle}>
              {t('Bills per Month')} - {getPeriodLabel()}
            </Typography>
            {loading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress size={40} color="primary" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={getMonthlyBillsChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333a56" />
                  <XAxis
                    dataKey="name"
                    stroke="#fff"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                  />
                  <YAxis stroke="#fff" allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#8884d8"
                    strokeWidth={2}
                    dot={{ fill: '#8884d8', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
